/**
 * SMTP Configuration Tab
 * 
 * Admin section to configure SMTP settings for sending invitation emails.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Input } from '../../components/ui';
import { smtpConfigService } from '../../services';
import type { SmtpConfig, UpdateSmtpConfigRequest } from '@budgira/shared';

type TestStatus = 'idle' | 'testing' | 'success' | 'error';

// Type for tracking original values (password excluded since it's never returned from API)
interface OriginalConfig {
  host: string;
  port: number;
  user: string;
  fromEmail: string;
  fromName: string;
  contactEmail: string;
  secure: boolean;
}

const SmtpConfigTab = () => {
  const { t } = useTranslation();
  
  // Form state
  const [config, setConfig] = useState<SmtpConfig | null>(null);
  const [originalConfig, setOriginalConfig] = useState<OriginalConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Form fields
  const [host, setHost] = useState('');
  const [port, setPort] = useState(587);
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [secure, setSecure] = useState(false);
  
  // Check if form has changes compared to original config
  const hasChanges = (): boolean => {
    // If password is filled, there's a change
    if (password) return true;
    
    // If no original config, check if any field has value
    if (!originalConfig) {
      return !!(host || user || fromEmail || fromName || contactEmail);
    }
    
    // Compare current values with original
    return (
      host !== originalConfig.host ||
      port !== originalConfig.port ||
      user !== originalConfig.user ||
      fromEmail !== originalConfig.fromEmail ||
      fromName !== originalConfig.fromName ||
      contactEmail !== originalConfig.contactEmail ||
      secure !== originalConfig.secure
    );
  };
  
  // Test connection state
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);
  
  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);
  
  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  const loadConfig = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await smtpConfigService.get();
      
      if (response.success && response.data) {
        const data = response.data;
        setConfig(data);
        
        // Set form values
        const hostValue = data.host || '';
        const portValue = data.port || 587;
        const userValue = data.user || '';
        const fromEmailValue = data.fromEmail || '';
        const fromNameValue = data.fromName || '';
        const contactEmailValue = data.contactEmail || '';
        const secureValue = data.secure ?? false;
        
        setHost(hostValue);
        setPort(portValue);
        setUser(userValue);
        setPassword(''); // Password is never returned from API
        setFromEmail(fromEmailValue);
        setFromName(fromNameValue);
        setContactEmail(contactEmailValue);
        setSecure(secureValue);
        
        // Store original values for change detection
        setOriginalConfig({
          host: hostValue,
          port: portValue,
          user: userValue,
          fromEmail: fromEmailValue,
          fromName: fromNameValue,
          contactEmail: contactEmailValue,
          secure: secureValue,
        });
      }
    } catch (err) {
      setError(t('admin.smtp.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsSaving(true);
    
    const updateData: UpdateSmtpConfigRequest = {
      host: host.trim(),
      port,
      user: user.trim(),
      fromEmail: fromEmail.trim(),
      fromName: fromName.trim(),
      contactEmail: contactEmail.trim(),
      secure,
    };
    
    // Only include password if it was changed
    if (password) {
      updateData.password = password;
    }
    
    try {
      const response = await smtpConfigService.update(updateData);
      
      if (response.success) {
        setSuccess(true);
        setPassword(''); // Clear password field after save
        setConfig(response.data || null);
        
        // Update original config to reflect saved values
        setOriginalConfig({
          host: host.trim(),
          port,
          user: user.trim(),
          fromEmail: fromEmail.trim(),
          fromName: fromName.trim(),
          contactEmail: contactEmail.trim(),
          secure,
        });
      } else {
        setError(response.error?.message || t('admin.smtp.errors.saveFailed'));
      }
    } catch {
      setError(t('admin.smtp.errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleTestConnection = async () => {
    setTestStatus('testing');
    setTestMessage(null);
    
    try {
      const response = await smtpConfigService.testConnection();
      
      if (response.success && response.data) {
        if (response.data.success) {
          setTestStatus('success');
          setTestMessage(t('admin.smtp.test.success'));
        } else {
          setTestStatus('error');
          setTestMessage(response.data.error || t('admin.smtp.test.failed'));
        }
      } else {
        setTestStatus('error');
        setTestMessage(response.error?.message || t('admin.smtp.test.failed'));
      }
    } catch {
      setTestStatus('error');
      setTestMessage(t('admin.smtp.test.failed'));
    }
  };
  
  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-48 animate-pulse" />
        <div className="h-96 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('admin.smtp.title')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('admin.smtp.description')}
        </p>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
      
      {/* Success message */}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {t('admin.smtp.saveSuccess')}
        </div>
      )}
      
      {/* Configuration form */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Server settings */}
          <div>
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
              {t('admin.smtp.sections.server')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('admin.smtp.fields.host')}
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="smtp.example.com"
                disabled={isSaving}
              />
              <Input
                label={t('admin.smtp.fields.port')}
                type="number"
                value={port}
                onChange={(e) => setPort(parseInt(e.target.value, 10) || 587)}
                min={1}
                max={65535}
                disabled={isSaving}
              />
            </div>
            
            <div className="mt-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={secure}
                  onChange={(e) => setSecure(e.target.checked)}
                  disabled={isSaving}
                  className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 rounded focus:ring-violet-500 bg-white dark:bg-slate-700"
                />
                <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">
                  {t('admin.smtp.fields.secure')}
                </span>
              </label>
              <p className="mt-1 ml-6 text-xs text-slate-500 dark:text-slate-400">
                {t('admin.smtp.fields.secureHelper')}
              </p>
            </div>
          </div>
          
          {/* Authentication */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
              {t('admin.smtp.sections.auth')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('admin.smtp.fields.user')}
                value={user}
                onChange={(e) => setUser(e.target.value)}
                placeholder="smtp_user"
                disabled={isSaving}
                autoComplete="username"
              />
              <Input
                label={t('admin.smtp.fields.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={config?.host ? '••••••••' : ''}
                disabled={isSaving}
                autoComplete="new-password"
                helperText={config?.host ? t('admin.smtp.fields.passwordHelper') : undefined}
              />
            </div>
          </div>
          
          {/* Sender settings */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
              {t('admin.smtp.sections.sender')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('admin.smtp.fields.fromEmail')}
                type="email"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="noreply@example.com"
                disabled={isSaving}
              />
              <Input
                label={t('admin.smtp.fields.fromName')}
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Budgira"
                disabled={isSaving}
              />
            </div>
          </div>
          
          {/* Contact form settings */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
              {t('admin.smtp.sections.contact')}
            </h3>
            <div className="max-w-md">
              <Input
                label={t('admin.smtp.fields.contactEmail')}
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="contact@example.com"
                disabled={isSaving}
                helperText={t('admin.smtp.fields.contactEmailHelper')}
              />
            </div>
          </div>
          
          {/* Test connection */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
              {t('admin.smtp.sections.test')}
            </h3>
            
            <div className="flex items-start gap-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleTestConnection}
                disabled={isSaving || testStatus === 'testing' || !host}
              >
                {testStatus === 'testing' ? t('admin.smtp.test.testing') : t('admin.smtp.test.button')}
              </Button>
              
              {testMessage && (
                <div
                  className={`flex-1 p-3 rounded-lg text-sm ${
                    testStatus === 'success'
                      ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                      : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {testStatus === 'success' ? (
                      <svg className="w-5 h-5 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    {testMessage}
                  </div>
                </div>
              )}
            </div>
            
            {!host && (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {t('admin.smtp.test.configureFirst')}
              </p>
            )}
          </div>
          
          {/* Submit button */}
          <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button type="submit" disabled={isSaving || !hasChanges()}>
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default SmtpConfigTab;
